import { convertTimeWithTimezone } from '@/utils/time';
import { BoxV2 as Box } from 'portal-commons';
import React from 'react';
import { CnpCampaignListItem } from '../../types';
import { remapTCRStatus } from '../../utils';
import { faHourglass, faSquareX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MoreButton from '@/shared_elements/components/MoreButton';
import { useHistory, useLocation } from 'react-router-dom';
import { CNP_RCS_CAMPAIGN_DETAILS_PATH } from '@/constants/paths';
import { capitalize, snakeToTitleCase } from '@/utils';
import { UsecaseType } from '@/utils/types';

interface Props {
  campaign: CnpCampaignListItem;
  rcsUseCases: UsecaseType[];
  onElect: (campaign: CnpCampaignListItem) => void;
  onDecline: (campaign: CnpCampaignListItem) => void;
}

const PendingRcsCnpCampaignListingRow: React.FC<Props> = ({
  campaign,
  rcsUseCases,
  onElect,
  onDecline,
}) => {
  const history = useHistory();
  const location = useLocation();

  const displayUsecase = (usecaseId: string) => {
    const usecase = rcsUseCases.find((useCase) => useCase.id === usecaseId);
    return usecase ? usecase.displayName : usecaseId;
  };
  const displayStatus = (status: string | undefined, isCapitalized = true) => {
    if (status === 'DECLINED' || !status) {
      return (
        <Box as="span" sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {!status && <FontAwesomeIcon icon={faHourglass} />}
          {status === 'DECLINED' && <FontAwesomeIcon icon={faSquareX} />}
          {!status ? 'PENDING' : 'REJECTED'}
        </Box>
      );
    }
    return isCapitalized ? capitalize(status) : status;
  };

  const handleRowClick = () => {
    history.push({
      pathname: CNP_RCS_CAMPAIGN_DETAILS_PATH.replace(':id', campaign.uid),
      state: {
        goBackPage: location.pathname + location.search,
      },
    });
  };

  return (
    <Box as="tr" data-testid="campaignRow" onClick={handleRowClick}>
      <Box as="td" sx={{ width: 100, minWidth: 100 }} data-testid="campaignUid">
        {campaign.uid}
      </Box>
      {/* <Box
        as="td"
        sx={{ width: 150, minWidth: 150 }}
        data-testid="campaignDisplayName"
      >
        {campaign?.displayName ?? 'N/A'}
      </Box> */}
      <Box
        as="td"
        sx={{ width: 80, minWidth: 80 }}
        data-testid="campaignBrandUid"
      >
        {campaign.brandUid}
      </Box>
      <Box
        as="td"
        sx={{ width: 100, minWidth: 100 }}
        data-testid="campaignUsecase"
      >
        {displayUsecase(campaign.usecase)}
      </Box>
      <Box
        as="td"
        sx={{ width: 120, minWidth: 120 }}
        data-testid="campaignBillingCategory"
      >
        {snakeToTitleCase(campaign.billingCategory)}
      </Box>
      <Box
        as="td"
        sx={{ width: 150, minWidth: 150, wordBreak: 'break-all' }}
        data-testid="campaignDownstreamCnpName"
      >
        {campaign.downstreamCnpName}
      </Box>
      <Box
        as="td"
        sx={{ width: 110, minWidth: 110 }}
        data-testid="campaignRegisteredOnDate"
      >
        {convertTimeWithTimezone(
          campaign.registeredOnDate,
          undefined,
          'MM/DD/YYYY'
        )}
      </Box>
      <Box
        as="td"
        sx={{ width: 110, minWidth: 110 }}
        data-testid="campaignSharedOnDate"
      >
        {convertTimeWithTimezone(
          campaign.sharedOnDate,
          undefined,
          'MM/DD/YYYY'
        )}
      </Box>
      <Box
        as="td"
        sx={{ width: 90, minWidth: 90 }}
        data-testid="campaignStatus"
      >
        {remapTCRStatus(campaign.status)}
      </Box>
      <Box
        as="td"
        sx={{
          position: 'relative',
          width: 145,
          minWidth: 145,
        }}
        data-testid="campaignSharedToStatus"
        onClick={(e) => e.stopPropagation()}
      >
        {displayStatus(campaign?.sharedToStatus)}
        {!campaign.sharedToStatus || campaign.sharedToStatus === 'DECLINED' ? (
          <Box
            as="span"
            sx={{
              position: 'absolute',
              right: 10,
              top: 'calc(50% + 2px)',
              transform: 'translateY(-50%)',
            }}
          >
            <MoreButton<CnpCampaignListItem>
              data={campaign}
              menuClass="tcr-menu"
              data-testid="campaignActionsButton"
              items={[
                {
                  label: `${
                    campaign.sharedToStatus === 'DECLINED'
                      ? 'Re-Elect'
                      : 'Elect'
                  }`,
                  onClick: () => onElect(campaign),
                },
                {
                  label: 'Decline',
                  hidden: campaign.sharedToStatus === 'DECLINED',
                  onClick: () => onDecline(campaign),
                },
              ]}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default PendingRcsCnpCampaignListingRow;
